//* Shared ledger config so the table, CSV and PDF always show the same columns/labels

//* brand colours shared by the modal and the PDF
export const BRAND = {
  primary: `#4B2EAD`,
  tint: `#EEEAF8`,
  //* light green for fund-addition (credit/IN) transaction rows
  creditTint: `#E8F5E9`,
  creditTintHover: `#DCEFDE`,
  credit: `#1b7a3d`,
}

export const PAYMENT_MODE_LABELS = {
  CASH: `Cash`,
  BANK_TRANSFER: `Bank Transfer`,
  JAZZ_CASH: `JazzCash`,
}

//* Type filter labels: API keeps IN/OUT; users see Credit (money into the fund) / Debit (money out)
export const TYPE_LABELS = { IN: `Credit`, OUT: `Debit` }

export const fmt = (n) =>
  Number(n || 0).toLocaleString(`en-PK`, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

export const modeLabel = (mode) => PAYMENT_MODE_LABELS[mode] || mode || `-`

//* local YYYY-MM-DD (not UTC) so dates match the user timezone
const toLocalDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, `0`)}-${String(
    d.getDate(),
  ).padStart(2, `0`)}`

//* current whole month: 1st -> last day
export const currentMonthRange = () => {
  const now = new Date()
  return {
    from: toLocalDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toLocalDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  }
}

//* Date preset options for the Funds Ledger filter UI
export const DATE_PRESET = {
  LAST_WEEK: `LAST_WEEK`,
  LAST_MONTH: `LAST_MONTH`,
  CUSTOM: `CUSTOM`,
}

export const DATE_PRESET_LABELS = {
  LAST_WEEK: `Last week`,
  LAST_MONTH: `Last month`,
  CUSTOM: `Custom`,
}

//* rolling last 7 days (today inclusive)
export const lastWeekRange = () => {
  const to = new Date()
  const from = new Date()
  from.setDate(to.getDate() - 6)
  return { from: toLocalDate(from), to: toLocalDate(to) }
}

//* previous calendar month: 1st -> last day
export const lastMonthRange = () => {
  const now = new Date()
  return {
    from: toLocalDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    to: toLocalDate(new Date(now.getFullYear(), now.getMonth(), 0)),
  }
}

//* resolve from/to for a preset
//* CUSTOM always starts on the current whole month; user can then tweak From/To
export const rangeForPreset = (preset, current = {}) => {
  if (preset === DATE_PRESET.LAST_WEEK) return lastWeekRange()
  if (preset === DATE_PRESET.LAST_MONTH) return lastMonthRange()
  return currentMonthRange()
}

//* key = row field, value(row) = display text; pdfWidth used by LedgerPdf (landscape A4)
//* wrap: only these columns may wrap, the rest stay on one line
export const LEDGER_COLUMNS = [
  { key: `date`, label: `Date`, pdfWidth: `6.5%` },
  { key: `fund`, label: `Fund (Allocated By → To)`, pdfWidth: `18%` },
  { key: `description`, label: `Description`, pdfWidth: `23%`, wrap: true },
  { key: `by`, label: `Entry Made By`, pdfWidth: `9%` },
  {
    key: `paymentMode`,
    label: `Payment Mode`,
    pdfWidth: `8.5%`,
    value: (r) => modeLabel(r?.paymentMode),
  },
  {
    key: `account`,
    label: `Account`,
    pdfWidth: `11%`,
    value: (r) => r?.account || `-`,
  },
  {
    key: `in`,
    label: `Credit`,
    pdfWidth: `8%`,
    numeric: true,
    value: (r) => (r?.in ? fmt(r.in) : ``),
  },
  {
    key: `out`,
    label: `Debit`,
    pdfWidth: `8%`,
    numeric: true,
    value: (r) => (r?.out ? fmt(r.out) : ``),
  },
  {
    key: `balance`,
    label: `Balance`,
    pdfWidth: `8%`,
    numeric: true,
    value: (r) => fmt(r?.balance),
  },
]

//* MUI cell style: keep non-wrapping columns on one line, give Description room
export const cellSx = (col) =>
  col.wrap ? { minWidth: 240 } : { whiteSpace: `nowrap` }

export const cellValue = (col, row) =>
  col.value ? col.value(row) : row?.[col.key] ?? ``

//* columns before the first amount column (used for colSpan of opening/totals rows)
export const LABEL_SPAN = LEDGER_COLUMNS.findIndex((c) => c.key === `in`)

//* API marks allocations / top-ups as type IN, expenses as OUT
export const isFundAddition = (row) => row?.type === `IN`

//* MUI TableRow highlight for fund-addition credit rows
export const fundAdditionRowSx = (row) =>
  isFundAddition(row)
    ? {
        bgcolor: BRAND.creditTint,
        "&:hover": { bgcolor: BRAND.creditTintHover },
      }
    : {}
